import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonTagDialog from './PersonTagDialog';
import { getPeopleTags, removePersonTag } from '../../services/userService';
import { TAG_GRID_COLUMNS } from '../../constants/personTagFormConstants';

const PersonTagsTab = ({ personId, autoOpenAdd = false, onAutoOpenAddHandled, onTagsChanged }) => {
  const dispatch = useDispatch();
  const canEditPerson = getFullPermissions('globals', 'People', 'any').Edit;
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tags, setTags] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadTags = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleTags(personId),
      onSuccess: (data) => setTags(data),
    });
  }, [dispatch, personId]);

  const handleAdd = useCallback(() => {
    setEditingTag(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingTag(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingTag(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadTags();
    onTagsChanged?.();
  }, [handleFormClose, loadTags, onTagsChanged]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${row.Type || 'tag'}" record?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => removePersonTag(row.ptsTagID),
            onSuccess: () => {
              dispatch(notify('Tag deleted successfully', 'success'));
              loadTags();
              onTagsChanged?.();
            },
          });
        },
        'Delete Tag'
      );
    },
    [dispatch, requestConfirm, loadTags, onTagsChanged]
  );

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useEffect(() => {
    if (!autoOpenAdd || loading) return;

    handleAdd();
    onAutoOpenAddHandled?.();
  }, [autoOpenAdd, loading, handleAdd, onAutoOpenAddHandled]);

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Tag"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={tags}
        columns={TAG_GRID_COLUMNS}
        getRowId={(row) => row.ptsTagID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Tags',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonTagDialog
          open={formOpen}
          personId={personId}
          tag={editingTag}
          existingTags={tags}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PersonTagsTab;
